import { Review } from "@/types/Reviews";
import { addNotification } from "@/utils/addNotification";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("api_token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      headers.set("Accept", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getReviews: builder.query<{ reviews: Review[]; last_id: number }, void>({
      query: () => ({
        method: "GET",
        url: "/reviews/get-all",
      }),
      transformResponse: (response: { data: { reviews: Review[]; last_id: number } }) => response.data,
    }),

    getNewReviews: builder.query<{ reviews: Review[]; last_id: number }, number>({
      query: (last_id) => ({ method: "GET", url: "/reviews/get-all", params: { start_id: last_id } }),
      //eslint-disable-next-line
      transformResponse: (response: any) => response.data,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        if (!data.reviews.length) return;
        dispatch(
          api.util.updateQueryData("getReviews", undefined, (draft) => {
            draft.reviews.unshift(...data.reviews);
            draft.last_id = data.last_id;
          }),
        );
      },
    }),

    getReviewsTree: builder.query<{ reviews: Review[] }, { entityType: string; entityId?: number }>({
      query: ({ entityType, entityId }) => ({
        method: "GET",
        url: "/reviews/get-reviews",
        params: {
          entity_type: entityType,
          ...(entityId !== undefined && { entity_id: entityId }),
        },
      }),
      //eslint-disable-next-line
      transformResponse: (response: any) => response.data,
    }),

    adminReply: builder.mutation<{ review: Review }, { review_id: number; text: string; name?: string }>({
      query: (body) => ({ url: "/reviews/admin-reply", method: "POST", body }),
      //eslint-disable-next-line
      transformResponse: (response: any) => response.data,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const review = data.review;

        dispatch(
          api.util.updateQueryData("getReviews", undefined, (draft) => {
            draft.reviews.push(review);
          }),
        );

        dispatch(
          api.util.updateQueryData(
            "getReviewsTree",
            { entityType: review.entity_type, entityId: review.entity_id },
            (draft) => {
              const parent = findReviewById(draft.reviews, review.parent_id!);
              if (parent) parent.replies.push({ ...review, replies: [] });
            },
          ),
        );
      },
    }),

    approveReview: builder.mutation<void, number>({
      query: (id) => ({ url: "/reviews/approve", method: "POST", body: { id } }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          api.util.updateQueryData("getReviews", undefined, (draft) => {
            addNotification({ type: "success", message: "Отзыв одобрен" });
            const review = draft.reviews.find((r) => r.id === id);
            if (review) review.status = "approved";
          }),
        );
      },
    }),

    rejectReview: builder.mutation<void, number>({
      query: (id) => ({ url: "/reviews/reject", method: "POST", body: { id } }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          api.util.updateQueryData("getReviews", undefined, (draft) => {
            addNotification({ type: "success", message: "Отзыв отклонен" });
            const review = draft.reviews.find((r) => r.id === id);
            if (review) review.status = "rejected";
          }),
        );
      },
    }),

    approveAll: builder.mutation<void, void>({
      query: () => ({ url: "/reviews/approve-all", method: "POST" }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          api.util.updateQueryData("getReviews", undefined, (draft) => {
            addNotification({ type: "success", message: "Все отзывы одобрены" });
            draft.reviews.forEach((r) => {
              if (r.status === "pending") r.status = "approved";
            });
          }),
        );
      },
    }),

    rejectAll: builder.mutation<void, void>({
      query: () => ({ url: "/reviews/reject-all", method: "POST" }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          api.util.updateQueryData("getReviews", undefined, (draft) => {
            addNotification({ type: "success", message: "Все отзывы отклонены" });
            draft.reviews.forEach((r) => {
              if (r.status === "pending") r.status = "rejected";
            });
          }),
        );
      },
    }),

    approveAndReplyAdmin: builder.mutation<{ review: Review }, { id: number; text: string; name?: string }>({
      query: (body) => ({ url: "/reviews/approve-and-reply-admin", method: "POST", body }),
      //eslint-disable-next-line
      transformResponse: (response: any) => response.data,
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          api.util.updateQueryData("getReviews", undefined, (draft) => {
            const review = draft.reviews.find((r) => r.id === body.id);
            if (review) review.status = "approved";
            draft.reviews.push(data.review);
          }),
        );
      },
    }),
  }),
});

function findReviewById(reviews: Review[], id: number): Review | undefined {
  for (const r of reviews) {
    if (r.id === id) return r;
    const found = findReviewById(r.replies, id);
    if (found) return found;
  }
  return undefined;
}

export const {
  useGetReviewsQuery,
  useGetNewReviewsQuery,
  useApproveReviewMutation,
  useRejectReviewMutation,
  useGetReviewsTreeQuery,
  useAdminReplyMutation,
  useApproveAllMutation,
  useRejectAllMutation,
  useApproveAndReplyAdminMutation,
} = api;
